import React, { useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import Spinner from '@components/Spinner';
import useAnchorLinkTracking from '@hooks/useAnchorLinkTracking';
import useEventTracking from '@hooks/useEventTracking';
import useRecommendedProjects from '@hooks/useRecommendedProjects';
import { TrackingLocation } from '@tracking';
import { impression } from '@tracking';
import { capitalizeTopic } from '@utils/formatting';

import ProjectCards from './ProjectCards';
import ForYouSection from './Section';
import { FOR_YOU_ENGAGEMENT_LABELS, FOR_YOU_SECTION_IDS } from './constants';
import useRegisterSection from './useRegisterSection';

interface ForYouDataToolsProps {
  mostReadTopic?: string;
}

const ForYouDataTools = ({ mostReadTopic }: ForYouDataToolsProps) => {
  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.DataTools,
  });
  const { projects, isLoadingProjects } = useRecommendedProjects(mostReadTopic);
  const projectCardsRef = useAnchorLinkTracking({
    enabled: !!projects,
    textSelector: 'h3',
    trackingLocation: TrackingLocation.DataTools,
  });

  const dataToolsDisplayText = useMemo(() => {
    if (!projects) return FOR_YOU_ENGAGEMENT_LABELS.NotEngaged.Pro.DataTools;

    const engaged = FOR_YOU_ENGAGEMENT_LABELS.Engaged.Pro.DataTools;
    const label = mostReadTopic
      ? `Related to ${capitalizeTopic(mostReadTopic)} Coverage`
      : engaged.label;

    return { ...engaged, label };
  }, [projects, mostReadTopic]);

  useRegisterSection(FOR_YOU_SECTION_IDS.DataTools, isLoadingProjects || !!projects);

  if (!isLoadingProjects && !projects) return null;

  return (
    <ForYouSection
      id={FOR_YOU_SECTION_IDS.DataTools}
      highlight={dataToolsDisplayText.label}
      subtitle={dataToolsDisplayText.subtitle}
      title={dataToolsDisplayText.title}
    >
      <Track>
        <InView
          onChange={(inView) => {
            inView && trackEvent(impression({ impressionElement: FOR_YOU_SECTION_IDS.DataTools }));
          }}
          triggerOnce
        >
          {!isLoadingProjects && projects ? (
            <div
              className="mt-32 flex w-full flex-row flex-wrap justify-center gap-32 md:grid md:grid-cols-3 md:gap-64"
              ref={projectCardsRef}
            >
              <ProjectCards projects={projects} />
            </div>
          ) : (
            <div className="grid w-full place-items-center">
              <Spinner marginY="small" />
            </div>
          )}
        </InView>
      </Track>
    </ForYouSection>
  );
};

export default ForYouDataTools;
